// Central configuration for subscription plans
// Single source of truth for plan names, pricing, features, and limits

import { BillingPeriod, PlanType } from '@/types/plans';

/**
 * Commercial names displayed in the UI
 * Maps internal plan_type to user-facing names
 */
export const PLAN_COMMERCIAL_NAMES: Record<PlanType, string> = {
  sponsor_free: 'Intro',
  sponsor_standard: 'Inspire',
  sponsor_premium: 'Infinity',
  sponsored_free: 'Intro',
  sponsored_premium: 'Infinity',
  // Legacy plans (not actively used)
  free: 'Gratuito',
  standard: 'Standard',
  premium: 'Premium',
} as const;

/**
 * Plan pricing in cents (BRL)
 * Organized by user role and billing period
 */
export const PLAN_PRICING = {
  sponsor: {
    sponsor_free: {
      weekly: 0,
      monthly: 0,
      quarterly: 0,
    },
    sponsor_standard: {
      weekly: 6990,    // R$ 69,90
      monthly: 14990,  // R$ 149,90
      quarterly: 39990 // R$ 399,90
    },
    sponsor_premium: {
      weekly: 9990,    // R$ 99,90
      monthly: 22990,  // R$ 229,90
      quarterly: 59990 // R$ 599,90
    },
  },
  sponsored: {
    sponsored_free: {
      weekly: 0,
      monthly: 0,
      quarterly: 0,
    },
    sponsored_premium: {
      weekly: 0,       // Not available weekly
      monthly: 4990,   // R$ 49,90
      quarterly: 12990 // R$ 129,90
    },
  },
} as const;

/**
 * Plan features and limits configuration
 * -1 means unlimited
 */
export interface PlanFeatureConfig {
  /** Daily profile view limit */
  profile_views_daily: number;
  /** Daily likes/connections limit */
  likes_daily: number;
  /** Daily new conversation initiation limit (sponsors only) */
  new_conversations_daily: number;
  /** Minimum profile age in days to view (0 = immediate, 1 = 24h+, 3 = 3 days+) */
  profile_priority_days: number;
  /** Access to advanced filters (habits, income, etc.) */
  advanced_filters: boolean;
  /** Can filter by "verified only" */
  verified_filter: boolean;
  /** Boosts included per billing period */
  boosts_included: {
    monthly: number;
    quarterly: number;
  };
  /** Profile highlighted in feed */
  highlight_in_feed: boolean;
  /** Can purchase additional boosts */
  can_buy_boosts: boolean;
}

export const PLAN_FEATURES: Record<string, PlanFeatureConfig> = {
  sponsor_free: {
    profile_views_daily: 30,
    likes_daily: 3,
    new_conversations_daily: 3,
    profile_priority_days: 3, // Only sees profiles 3+ days old
    advanced_filters: false,
    verified_filter: false,
    boosts_included: { monthly: 0, quarterly: 0 },
    highlight_in_feed: false,
    can_buy_boosts: false,
  },
  sponsor_standard: {
    profile_views_daily: -1, // Unlimited
    likes_daily: 30,
    new_conversations_daily: -1, // Unlimited
    profile_priority_days: 1, // Sees profiles 24h+ old
    advanced_filters: true,
    verified_filter: false,
    boosts_included: { monthly: 1, quarterly: 3 },
    highlight_in_feed: false,
    can_buy_boosts: true,
  },
  sponsor_premium: {
    profile_views_daily: -1, // Unlimited
    likes_daily: -1, // Unlimited
    new_conversations_daily: -1, // Unlimited
    profile_priority_days: 0, // Sees new profiles immediately
    advanced_filters: true,
    verified_filter: true, // Only Infinity sponsors can filter verified
    boosts_included: { monthly: 4, quarterly: 12 },
    highlight_in_feed: true,
    can_buy_boosts: true,
  },
  sponsored_free: {
    profile_views_daily: -1, // Unlimited
    likes_daily: -1, // Unlimited
    new_conversations_daily: -1, // Unlimited
    profile_priority_days: 0,
    advanced_filters: false,
    verified_filter: true, // All sponsored can filter verified
    boosts_included: { monthly: 0, quarterly: 0 },
    highlight_in_feed: false,
    can_buy_boosts: true,
  },
  sponsored_premium: {
    profile_views_daily: -1, // Unlimited
    likes_daily: -1, // Unlimited
    new_conversations_daily: -1, // Unlimited
    profile_priority_days: 0,
    advanced_filters: true,
    verified_filter: true, // All sponsored can filter verified
    boosts_included: { monthly: 0, quarterly: 0 },
    highlight_in_feed: true,
    can_buy_boosts: true,
  },
  // Legacy plans (fallback to sponsor_free)
  free: {
    profile_views_daily: 30,
    likes_daily: 3,
    new_conversations_daily: 3,
    profile_priority_days: 3,
    advanced_filters: false,
    verified_filter: false,
    boosts_included: { monthly: 0, quarterly: 0 },
    highlight_in_feed: false,
    can_buy_boosts: false,
  },
  standard: {
    profile_views_daily: -1,
    likes_daily: 30,
    new_conversations_daily: -1,
    profile_priority_days: 1,
    advanced_filters: true,
    verified_filter: false,
    boosts_included: { monthly: 1, quarterly: 3 },
    highlight_in_feed: false,
    can_buy_boosts: true,
  },
  premium: {
    profile_views_daily: -1,
    likes_daily: -1,
    new_conversations_daily: -1,
    profile_priority_days: 0,
    advanced_filters: true,
    verified_filter: true,
    boosts_included: { monthly: 4, quarterly: 12 },
    highlight_in_feed: true,
    can_buy_boosts: true,
  },
} as const;

/**
 * Helper: Get price for a specific plan and billing period
 */
export const getPlanPrice = (
  planType: PlanType,
  billingPeriod: BillingPeriod,
  role: 'sponsor' | 'sponsored'
): number => {
  const rolePricing = PLAN_PRICING[role];
  if (!rolePricing) return 0;
  
  const planPricing = rolePricing[planType as keyof typeof rolePricing];
  if (!planPricing) return 0;
  
  return planPricing[billingPeriod] || 0;
};

/**
 * Helper: Get commercial name for a plan
 */
export const getPlanCommercialName = (planType: string): string => {
  return PLAN_COMMERCIAL_NAMES[planType as PlanType] || planType;
};

/**
 * Helper: Get features for a plan
 */
export const getPlanFeatures = (planType: string): PlanFeatureConfig => {
  return PLAN_FEATURES[planType] || PLAN_FEATURES.sponsor_free;
};

/**
 * Helper: Check if a limit is unlimited
 */
export const isUnlimited = (limit: number): boolean => {
  return limit === -1;
};

/**
 * Convert cents to reais for display
 */
export const centsToReais = (cents: number): number => cents / 100;

/**
 * Convert reais to cents for backend
 */
export const reaisToCents = (reais: number): number => Math.round(reais * 100);
